import React, { useState, useEffect } from "react";
import { useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Stepper,
  Step,
  StepLabel,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  Grid,
  Button,
  Divider,
  InputAdornment,
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SearchIcon from "@mui/icons-material/Search";
import { getCPList, checkDuplicatePlanName } from "../../Services/Brokerage";

const steps = [
  "Plan Details",
   "POP Validity",
  "Scope Type",
  "Scope Selection",
 
  "Calculation Rules",
  "Eligibility",
  "Conflict Settings",
  "CP Sign-Off",
  "Review",
];

const CreatePopPlanStep1 = ({ data, setData, onNext }) => {
  const debounceRef = useRef(null);
  const [channelPartners, setChannelPartners] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [duplicateNameError, setDuplicateNameError] = useState("");

  const togglePartner = (id) => {
    setData((prev) => ({
      ...prev,
      selectedChannelPartners: prev.selectedChannelPartners.includes(id)
        ? prev.selectedChannelPartners.filter((p) => p !== id)
        : [...prev.selectedChannelPartners, id],
    }));
  };

  const handleNext = () => {
    if (!data.planName?.trim()) return;
    if (
      data.channelPartnerType === "specific" &&
      data.selectedChannelPartners.length === 0
    )
      return;
    onNext();
  };

const handleNameChange = (e) => {
  const name = e.target.value;
  setData((prev) => ({ ...prev, planName: name }));

  // Clear error if input is empty
  if (name.trim() === "") {
    setDuplicateNameError("");
    return;
  }

  // Debounce API call to avoid too many requests
  if (debounceRef.current) clearTimeout(debounceRef.current);

  debounceRef.current = setTimeout(async () => {
    try {
      const existingPlan = await checkDuplicatePlanName(name.trim());
      if (existingPlan) {
        setDuplicateNameError("POP plan with this name already exists");
      } else {
        setDuplicateNameError(""); // ✅ clear error if name is unique
      }
    } catch (err) {
      console.error("Error checking plan name:", err);
      setDuplicateNameError(""); // optional fallback
    }
  }, 500); // wait 500ms after typing stops
};

  useEffect(() => {
    const fetchCPs = async () => {
      const res = await getCPList();
      const formatted = res.map((cp) => ({
        id: cp.cpId,
        name: cp.billingName,
      }));
      setChannelPartners(formatted);
    };
    fetchCPs();
  }, []);

  const filteredCPs = channelPartners.filter((cp) =>
    cp.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box sx={{ p: 1 }}>
      {/* Stepper */}
       <Stepper activeStep={0} alternativeLabel sx={{ mb: 3 }}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel
                      StepIconProps={{
                        sx: {
                          width: 36,
                          height: 36,
                          fontSize: "1rem",
                          "&.Mui-completed": { color: "#2563EB" },
                          "&.Mui-active": {
                            color: "#2563EB",
                            transform: "scale(1.15)",
                          },
                        },
                      }}
                      sx={{
                        "& .MuiStepLabel-label": {
                          fontSize: "0.85rem",
                          mt: 1,
                        },
                      }}
                    >
                      {label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>

      <Typography variant="subtitle2" color="text.secondary" mb={2}>
        Step 1 of 9: <strong>Plan Details</strong> — Name & CPs
      </Typography>

      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <CheckCircleIcon color="success" sx={{ mr: 1 }} />
            <Typography fontWeight={600}>Plan Details</Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" mb={3}>
            Set a name for this POP and select which Channel Partners it applies to.
          </Typography>

          {/* Plan Name */}
          <Box sx={{ mb: 4, maxWidth: 520 }}>
            <Typography variant="subtitle2" gutterBottom>
              POP Plan Name *
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={data.planName}
              onChange={handleNameChange}
              helperText={
                duplicateNameError || "A descriptive name to identify this plan easily."
              }
              error={!!duplicateNameError}
              FormHelperTextProps={{
                sx: {
                  fontSize: "1.6rem", // Adjust this value for the desired font size
                  color: duplicateNameError ? "#d32f2f" : "#6B7280", // Optional: customize color
                },
              }}
            />
          </Box>

          {/* Channel Partners */}
          <Typography variant="subtitle2" gutterBottom>
            Channel Partners
          </Typography>

          {/* All CP Card */}
          <Card
            variant="outlined"
            sx={{
              mb: 2,
              cursor: "pointer",
            }}
            onClick={() =>
              setData((prev) => ({
                ...prev,
                channelPartnerType: "all",
                selectedChannelPartners: [],
              }))
            }
          >
            <CardContent>
              <FormControlLabel
                control={<Radio checked={data.channelPartnerType === "all"} />}
                label={
                  <Box>
                    <Typography fontWeight={500}>
                      All Channel Partners
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      This POP will apply to all registered CPs.
                    </Typography>
                  </Box>
                }
              />
            </CardContent>
          </Card>

          {/* Specific CP Card */}
          <Card
            variant="outlined"
            sx={{
              cursor: "pointer",
              borderColor:
                data.channelPartnerType === "specific"
                  ? "#c4d9f5"
                  : "divider",
              bgcolor:
                data.channelPartnerType === "specific"
                  ? "#EFF6FF"
                  : "inherit",
            }}
            onClick={() =>
              setData((prev) => ({
                ...prev,
                channelPartnerType: "specific",
              }))
            }
          >
            <CardContent>
              <FormControlLabel
                control={<Radio checked={data.channelPartnerType === "specific"} />}
                label={
                  <Box>
                    <Typography fontWeight={500}>
                      Specific Channel Partners
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Select individual CPs for this POP.
                    </Typography>
                  </Box>
                }
              />
            </CardContent>
          </Card>

          {/* Specific CP List */}
          {data.channelPartnerType === "specific" && (
            <>
              <Divider sx={{ my: 3 }} />

              {/* Search Box */}
              <Box
                sx={{
                  mb: 2,
                  display: "flex",
                  justifyContent: "flex-end",
                  marginRight: 5,
                }}
              >
                <Box sx={{ width: 300 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search Channel Partner..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon
                            sx={{
                              fontSize: 20,
                              color: "#6B7280",
                            }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Box>
              <Divider sx={{ mb: 2, borderColor: "#E5E7EB" }} />

              <Grid
                container
                spacing={2}
                sx={{ maxHeight: 260, overflowY: "auto", pr: 1 }}
              >
                {filteredCPs.map((cp) => {
                  const selected =
                    data.selectedChannelPartners.includes(cp.id);

                  return (
                    <Grid item xs={12} sm={6} key={cp.id}>
                      <Card
                        variant="outlined"
                        sx={{
                          cursor: "pointer",
                          borderColor: selected ? "#c4d9f5" : "divider",
                          bgcolor: selected ? "#EFF6FF" : "white",
                          transition: "0.2s",
                        }}
                        onClick={() => togglePartner(cp.id)}
                      >
                        <CardContent
                          sx={{
                            display: "flex",
                            gap: 1.5,
                            py: 1.5,
                          }}
                        >
                          <Checkbox checked={selected} />
                          <Typography fontWeight={500}>
                            {cp.name}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block" }}
              >
                {data.selectedChannelPartners.length} CP(s) selected
              </Typography>
            </>
          )}
        </CardContent>
      </Card>

      {/* Footer */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
        <Button variant="outlined" disabled>
          ← Back
        </Button>
        <Button variant="contained" onClick={handleNext}>
          Next →
        </Button>
      </Box>
    </Box>
  );
};

export default CreatePopPlanStep1;
